import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import {
  getIndustryLabel,
  REQUEST_STATUS_LABELS,
  REQUEST_STATUS_STYLES,
} from "@/constants/partnerAiEmployeeRequestOptions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const formatSubmittedDate = (isoDate) => {
  if (!isoDate) return "N/A";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const ListSkeleton = () => (
  <div className="rounded-2xl border border-partner bg-[#11151F] p-6 animate-pulse space-y-3">
    <div className="h-5 w-48 bg-white/5 rounded" />
    <div className="h-12 bg-white/5 rounded" />
    <div className="h-12 bg-white/5 rounded" />
  </div>
);

const PartnerAiEmployeeRequestsList = ({ refreshKey = 0 }) => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isRetrying, setIsRetrying] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoadError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/partner/ai-employee-requests`, {
        headers: getPartnerAuthHeaders(),
      });
      setRequests(res.data?.requests || []);
    } catch (err) {
      setRequests([]);
      setLoadError(
        extractErrorMessage(err, "Could not load your AI Employee requests.")
      );
    }
  }, []);

  useEffect(() => {
    if (!getPartnerAuthHeaders().Authorization) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchRequests().finally(() => setIsLoading(false));
  }, [fetchRequests, refreshKey]);

  const handleRetry = async () => {
    setIsRetrying(true);
    await fetchRequests();
    setIsRetrying(false);
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div data-testid="partner-ai-employee-requests">
        <h2 className="text-lg font-bold text-partner mb-4">Your AI Employee requests</h2>
        <ListSkeleton />
      </div>
    );
  }

  return (
    <div data-testid="partner-ai-employee-requests">
      <h2 className="text-lg font-bold text-partner mb-4">Your AI Employee requests</h2>

      {loadError && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-center mb-4">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-3" />
          <p className="text-sm text-partner-muted font-body mb-4">{loadError}</p>
          <button
            type="button"
            onClick={handleRetry}
            disabled={isRetrying}
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold bg-white/10 text-partner hover:bg-white/20 disabled:opacity-50"
          >
            {isRetrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Retry
          </button>
        </div>
      )}

      {!loadError && requests.length === 0 && (
        <div className="rounded-2xl border border-partner bg-[#11151F] p-8 text-center">
          <p className="text-sm text-partner-muted font-body">No requests yet.</p>
        </div>
      )}

      {!loadError && requests.length > 0 && (
        <div className="rounded-2xl border border-partner bg-[#11151F] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]" data-testid="partner-ai-employee-requests-table">
              <thead>
                <tr className="border-b border-partner text-left">
                  {["AI Employee", "Industry", "Status", "Submitted", "First client"].map((col) => (
                    <th
                      key={col}
                      className="px-4 sm:px-5 py-3 text-[10px] font-mono tracking-wider text-partner-muted uppercase"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr
                    key={req.id}
                    className="border-b border-partner last:border-b-0 hover:bg-[#080A0F]/50"
                  >
                    <td className="px-4 sm:px-5 py-4">
                      <p className="text-sm font-semibold text-partner">{req.vertical_name}</p>
                      {req.status === "approved" && (
                        <p className="text-[10px] text-partner-muted font-body mt-1">
                          Contact support to enable deploy for this AI Employee.
                        </p>
                      )}
                    </td>
                    <td className="px-4 sm:px-5 py-4 text-sm text-partner-muted font-body">
                      {getIndustryLabel(req.industry_category)}
                    </td>
                    <td className="px-4 sm:px-5 py-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-body border capitalize ${
                          REQUEST_STATUS_STYLES[req.status] || REQUEST_STATUS_STYLES.submitted
                        }`}
                      >
                        {REQUEST_STATUS_LABELS[req.status] || req.status}
                      </span>
                    </td>
                    <td className="px-4 sm:px-5 py-4 text-sm text-partner-muted font-body">
                      {formatSubmittedDate(req.created_at)}
                    </td>
                    <td className="px-4 sm:px-5 py-4 text-sm text-partner-muted font-body">
                      {req.first_client_name || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerAiEmployeeRequestsList;
